/**
 * Timestamp formatting for chat messages.
 *
 * Displays relative time ("2 min ago") for recent messages
 * and absolute time for older ones. Used both inline on
 * individual messages and as group separators.
 */

/** Threshold in milliseconds: messages newer than this show relative time */
const RELATIVE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Minimum interval between timestamp group separators (in ms) */
export const TIMESTAMP_GROUP_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Format a timestamp for display.
 *
 * - Under 1 minute: "Just now"
 * - Under 1 hour: "X min ago"
 * - Under 24 hours: "X hr ago"
 * - Older: "Mar 30, 2:15 PM"
 *
 * @param timestampMs - Unix timestamp in milliseconds
 * @param now - Current time in ms (injectable for testing)
 * @returns Formatted time string
 */
export function formatTimestamp(
  timestampMs: number,
  now: number = Date.now(),
): string {
  const diffMs = now - timestampMs;

  if (diffMs < 0) {
    return "Just now";
  }

  if (diffMs < 60_000) {
    return "Just now";
  }

  if (diffMs < 3_600_000) {
    const mins = Math.floor(diffMs / 60_000);
    return `${mins} min ago`;
  }

  if (diffMs < RELATIVE_THRESHOLD_MS) {
    const hrs = Math.floor(diffMs / 3_600_000);
    return `${hrs} hr ago`;
  }

  // Absolute format for older messages
  const date = new Date(timestampMs);
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${month} ${day}, ${time}`;
}

/**
 * Determine whether a timestamp separator should be shown
 * between two consecutive messages.
 *
 * A separator is shown when more than TIMESTAMP_GROUP_INTERVAL_MS
 * has elapsed between messages.
 *
 * @param prevTimestamp - Previous message timestamp (ms)
 * @param currentTimestamp - Current message timestamp (ms)
 * @returns true if a separator should be inserted
 */
export function shouldShowTimestamp(
  prevTimestamp: number,
  currentTimestamp: number,
): boolean {
  return currentTimestamp - prevTimestamp >= TIMESTAMP_GROUP_INTERVAL_MS;
}

/**
 * Create a timestamp separator DOM element.
 *
 * @param timestampMs - Unix timestamp in milliseconds
 * @returns HTMLElement for the timestamp separator
 */
export function createTimestampElement(timestampMs: number): HTMLElement {
  const el = document.createElement("div");
  el.className = "hana-timestamp";
  el.setAttribute("role", "separator");
  el.setAttribute(
    "aria-label",
    `Messages from ${formatTimestamp(timestampMs)}`,
  );
  el.textContent = formatTimestamp(timestampMs);
  return el;
}
